/* Dashboard: catalogo dei diagrammi raggruppati per cliente. Punto d'ingresso
   dell'app multi-diagramma — da qui si apre o si crea un diagramma. */
import { api, ApiError, type ClientSummary, type DiagramSummary, type User } from "../api.js";
import { el } from "../render/svg.js";
import { buildAppBar } from "./appbar.js";

const fmt = (iso: string): string => new Date(iso).toLocaleString("it-IT");

export function mountDashboard(root: HTMLElement, user: User, onOpen: (id: number) => void): void {
  const canEdit = user.role === "admin" || user.role === "editor";
  const isAdmin = user.role === "admin";
  root.innerHTML = "";
  const wrap = el("div", "dash-wrap");
  root.appendChild(wrap);

  const setError = (host: HTMLElement, e: unknown): void => {
    host.appendChild(el("p", "login-err", e instanceof ApiError ? e.message : "errore di rete"));
  };

  async function load(): Promise<void> {
    wrap.textContent = "";

    // intestazione
    wrap.appendChild(buildAppBar({ user, sticky: true }));

    const loading = el("p", "hint", "Caricamento…");
    wrap.appendChild(loading);

    let clients: ClientSummary[];
    let diagrams: DiagramSummary[];
    try {
      [clients, diagrams] = await Promise.all([api.listClients(), api.listDiagrams()]);
    } catch (e) {
      loading.remove();
      setError(wrap, e);
      return;
    }
    loading.remove();

    if (canEdit) wrap.appendChild(buildToolbar(clients));

    // gruppi per cliente (inclusi quelli senza diagrammi)
    const byClient = new Map<number, DiagramSummary[]>();
    for (const d of diagrams) {
      const arr = byClient.get(d.clientId) ?? [];
      arr.push(d);
      byClient.set(d.clientId, arr);
    }
    if (!clients.length) {
      wrap.appendChild(el("p", "hint", "Nessun cliente. " + (isAdmin ? "Creane uno per iniziare." : "")));
    }
    for (const c of clients) {
      wrap.appendChild(buildClientSection(c, byClient.get(c.id) ?? []));
    }
  }

  /** Blocco etichettato: il titolo dice a quale azione appartengono i campi. */
  function buildGroup(label: string, form: HTMLFormElement, secondary = false): HTMLElement {
    const g = el("div", "dash-group" + (secondary ? " secondary" : ""));
    g.appendChild(el("span", "dash-group-label", label));
    g.appendChild(form);
    return g;
  }

  function buildToolbar(clients: ClientSummary[]): HTMLElement {
    const bar = el("div", "dash-toolbar");

    // nuovo diagramma
    const form = el("form", "dash-newdiagram") as HTMLFormElement;
    const sel = el("select", "dash-select") as HTMLSelectElement;
    for (const c of clients) {
      const o = document.createElement("option");
      o.value = String(c.id);
      o.textContent = c.name;
      sel.appendChild(o);
    }
    const title = el("input", "dash-input") as HTMLInputElement;
    title.placeholder = "Titolo";
    title.required = true;
    const btn = el("button", "btn sm", "Crea") as HTMLButtonElement;
    btn.type = "submit";
    const err = el("span", "login-err");
    err.hidden = true;
    form.append(sel, title, btn, err);
    if (!clients.length) {
      sel.disabled = true;
      title.disabled = true;
      btn.disabled = true;
    }
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.hidden = true;
      btn.disabled = true;
      try {
        const created = await api.createDiagram({ clientId: Number(sel.value), title: title.value.trim() });
        onOpen(created.id);
      } catch (ex) {
        err.textContent = ex instanceof ApiError ? ex.message : "errore";
        err.hidden = false;
        btn.disabled = false;
      }
    });
    bar.appendChild(buildGroup("Nuovo diagramma", form));

    // nuovo cliente (solo admin)
    if (isAdmin) {
      const cf = el("form", "dash-newclient") as HTMLFormElement;
      const name = el("input", "dash-input") as HTMLInputElement;
      name.placeholder = "Nome";
      name.required = true;
      const cbtn = el("button", "btn sm ghost", "Aggiungi") as HTMLButtonElement;
      cbtn.type = "submit";
      const cerr = el("span", "login-err");
      cerr.hidden = true;
      cf.append(name, cbtn, cerr);
      cf.addEventListener("submit", async (e) => {
        e.preventDefault();
        cerr.hidden = true;
        cbtn.disabled = true;
        try {
          await api.createClient(name.value.trim());
          await load();
        } catch (ex) {
          cerr.textContent = ex instanceof ApiError ? ex.message : "errore";
          cerr.hidden = false;
          cbtn.disabled = false;
        }
      });
      bar.appendChild(buildGroup("Nuovo cliente", cf, true));
    }
    return bar;
  }

  function buildClientSection(c: ClientSummary, diags: DiagramSummary[]): HTMLElement {
    const sec = el("section", "dash-client");
    const h = el("div", "dash-client-head");
    h.appendChild(el("h2", "dash-client-name", c.name));
    h.appendChild(el("span", "dash-client-slug", `${diags.length} diagramma/i`));
    sec.appendChild(h);

    if (!diags.length) {
      sec.appendChild(el("p", "hint", "Nessun diagramma per questo cliente."));
      return sec;
    }
    const grid = el("div", "dash-grid");
    for (const d of diags) grid.appendChild(buildCard(d));
    sec.appendChild(grid);
    return sec;
  }

  function buildCard(d: DiagramSummary): HTMLElement {
    const card = el("div", "dash-card");
    card.appendChild(el("div", "dash-card-title", d.title));
    card.appendChild(el("div", "dash-card-sub", `agg. ${fmt(d.updatedAt)}`));
    if (d.share.visibility === "public") {
      const pub = el("a", "dash-card-pub", "⇗ pubblicato") as HTMLAnchorElement;
      pub.href = `/p/${d.share.slug ?? ""}`;
      pub.target = "_blank";
      pub.rel = "noopener noreferrer";
      pub.title = "apri il link pubblico";
      card.appendChild(pub);
    }
    if (d.lock) card.appendChild(el("div", "dash-card-lock", `in modifica da ${d.lock.name ?? "un utente"}`));

    const actions = el("div", "dash-card-actions");
    const open = el("button", "btn sm", "Apri") as HTMLButtonElement;
    open.type = "button";
    open.addEventListener("click", () => onOpen(d.id));
    actions.appendChild(open);

    if (isAdmin) {
      const del = el("button", "btn sm danger", "Elimina") as HTMLButtonElement;
      del.type = "button";
      let armed = false;
      del.addEventListener("click", async () => {
        if (!armed) {
          armed = true;
          del.textContent = "Confermi?";
          setTimeout(() => {
            armed = false;
            del.textContent = "Elimina";
          }, 3000);
          return;
        }
        del.disabled = true;
        try {
          await api.deleteDiagram(d.id);
          await load();
        } catch (ex) {
          del.disabled = false;
          setError(card, ex);
        }
      });
      actions.appendChild(del);
    }
    card.appendChild(actions);
    return card;
  }

  void load();
}
